import { useState } from 'react';
import { Database, CheckCircle, XCircle, AlertCircle, RefreshCw, Plus } from 'lucide-react';
import { simpleFirestoreService } from '../utils/simpleFirestoreService';

const FirestoreTest = () => {
  const [isTestingConnection, setIsTestingConnection] = useState(false);
  const [isTestingAdd, setIsTestingAdd] = useState(false);
  const [isLoadingCars, setIsLoadingCars] = useState(false);
  const [connectionResult, setConnectionResult] = useState<any>(null);
  const [addResult, setAddResult] = useState<any>(null);
  const [carsData, setCarsData] = useState<any>(null);

  const configInfo = { 
    service: 'Simple Firestore Service (Backend Proxy)', 
    endpoint: 'http://localhost:3002',
    projectId: 'precise-crowbar-474919-j5',
    baseUrl: 'http://localhost:3002/api/firestore',
    hasAccessToken: true,
    isConfigured: true
  };

  const testConnection = async () => {
    setIsTestingConnection(true);
    setConnectionResult(null);
    
    try {
      const result = await simpleFirestoreService.testConnection();
      setConnectionResult(result);
    } catch (error) {
      setConnectionResult({
        success: false,
        error: 'Test failed with error',
        data: error
      });
    } finally {
      setIsTestingConnection(false);
    }
  };

  const testAddCar = async () => {
    setIsTestingAdd(true);
    setAddResult(null);
    
    try {
      const testCar = {
        make: 'Toyota',
        model: 'Camry Test',
        year: 2023,
        price: 25000,
        mileage: 1000,
        color: 'Blue',
        fuelType: 'Gasoline' as const,
        transmission: 'Automatic' as const,
        condition: 'Used' as const,
        description: 'Test car added from Firestore integration',
        images: ['https://storage.googleapis.com/roke-car-images/car-images/test-image.jpg'],
        features: ['Test Feature 1', 'Test Feature 2'],
        saleStatus: 'available' as const,
        createdAt: new Date(),
        updatedAt: new Date(),
        isAvailable: true
      };

      const result = await simpleFirestoreService.addCar(testCar);
      setAddResult(result);
    } catch (error) {
      setAddResult({
        success: false,
        error: 'Add test failed with error',
        data: error
      });
    } finally {
      setIsTestingAdd(false);
    }
  };

  const loadAllCars = async () => {
    setIsLoadingCars(true);
    setCarsData(null);
    
    try {
      const result = await simpleFirestoreService.getAllCars();
      setCarsData(result);
    } catch (error) {
      setCarsData({
        success: false,
        error: 'Load cars failed',
        data: error
      });
    } finally {
      setIsLoadingCars(false);
    }
  };

  const StatusIcon = ({ success }: { success?: boolean }) => {
    if (success === undefined) return <AlertCircle className="h-5 w-5 text-gray-400" />;
    return success ? 
      <CheckCircle className="h-5 w-5 text-green-600" /> : 
      <XCircle className="h-5 w-5 text-red-600" />;
  };

  return (
    <div className="max-w-4xl mx-auto p-6 space-y-6">
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex items-center space-x-3 mb-6">
          <Database className="h-6 w-6 text-orange-600" />
          <h2 className="text-xl font-semibold text-gray-900">
            Google Cloud Firestore Connection Test
          </h2>
        </div>

        {/* Configuration Info */}
        <div className="mb-6">
          <h3 className="text-lg font-medium text-gray-900 mb-3">Current Configuration</h3>
          <div className="bg-gray-50 rounded-lg p-4 space-y-2">
            <div className="flex justify-between">
              <span className="text-sm font-medium text-gray-600">Database:</span>
              <span className="text-sm text-gray-900 flex items-center">
                <Database className="h-4 w-4 mr-1 text-orange-600" />
                Google Cloud Firestore
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm font-medium text-gray-600">Project ID:</span>
              <span className="text-sm text-gray-900">{configInfo.projectId}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm font-medium text-gray-600">Base URL:</span>
              <span className="text-sm text-gray-900 break-all">{configInfo.baseUrl}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm font-medium text-gray-600">Access Token:</span>
              <div className="flex items-center space-x-2">
                <StatusIcon success={configInfo.hasAccessToken} />
                <span className="text-sm text-gray-900">
                  {configInfo.hasAccessToken ? 'Configured' : 'Not configured'}
                </span>
              </div>
            </div>
            <div className="flex justify-between">
              <span className="text-sm font-medium text-gray-600">Overall Status:</span>
              <div className="flex items-center space-x-2">
                <StatusIcon success={configInfo.isConfigured} />
                <span className="text-sm text-gray-900">
                  {configInfo.isConfigured ? 'Ready' : 'Needs configuration'}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Connection Test */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-lg font-medium text-gray-900">Database Connection</h3>
            <button
              onClick={testConnection}
              disabled={isTestingConnection || !configInfo.isConfigured}
              className="btn-primary flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isTestingConnection ? (
                <RefreshCw className="h-4 w-4 animate-spin" />
              ) : (
                <Database className="h-4 w-4" />
              )}
              <span>
                {isTestingConnection ? 'Testing...' : 'Test Connection'}
              </span>
            </button>
          </div>

          {connectionResult && (
            <div className={`p-4 rounded-lg border ${
              connectionResult.success 
                ? 'bg-green-50 border-green-200' 
                : 'bg-red-50 border-red-200'
            }`}>
              <div className="flex items-start space-x-3">
                <StatusIcon success={connectionResult.success} />
                <div className="flex-1">
                  <p className={`font-medium ${
                    connectionResult.success ? 'text-green-800' : 'text-red-800'
                  }`}>
                    {connectionResult.success ? connectionResult.data?.message : 'Connection failed'}
                  </p>
                  
                  {connectionResult.success && connectionResult.data && (
                    <div className="mt-2 text-sm text-green-700">
                      <p><strong>Project:</strong> {connectionResult.data.projectId}</p>
                      <p><strong>Documents:</strong> {connectionResult.data.documentsCount}</p>
                    </div>
                  )}
                  
                  {connectionResult.error && (
                    <pre className="mt-2 text-xs text-red-700 bg-red-100 p-2 rounded overflow-x-auto">
                      {connectionResult.error}
                    </pre>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Add Car Test */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-lg font-medium text-gray-900">Add Car Test</h3>
            <button
              onClick={testAddCar}
              disabled={isTestingAdd || !connectionResult?.success}
              className="btn-secondary flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isTestingAdd ? (
                <RefreshCw className="h-4 w-4 animate-spin" />
              ) : (
                <Plus className="h-4 w-4" />
              )}
              <span>
                {isTestingAdd ? 'Adding Test Car...' : 'Add Test Car'}
              </span>
            </button>
          </div>

          {addResult && (
            <div className={`p-4 rounded-lg border ${
              addResult.success 
                ? 'bg-green-50 border-green-200' 
                : 'bg-red-50 border-red-200'
            }`}>
              <div className="flex items-start space-x-3">
                <StatusIcon success={addResult.success} />
                <div className="flex-1">
                  <p className={`font-medium ${
                    addResult.success ? 'text-green-800' : 'text-red-800'
                  }`}>
                    {addResult.success ? 'Test car added successfully!' : 'Failed to add test car'}
                  </p>
                  
                  {addResult.success && addResult.data && (
                    <div className="mt-2 text-sm text-green-700">
                      <p><strong>Car ID:</strong> {addResult.data.id}</p>
                      <p><strong>Make:</strong> {addResult.data.car.make}</p>
                      <p><strong>Model:</strong> {addResult.data.car.model}</p>
                    </div>
                  )}
                  
                  {addResult.error && (
                    <pre className="mt-2 text-xs text-red-700 bg-red-100 p-2 rounded overflow-x-auto">
                      {addResult.error}
                    </pre>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Load All Cars */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-lg font-medium text-gray-900">Load All Cars</h3>
            <button
              onClick={loadAllCars}
              disabled={isLoadingCars || !configInfo.isConfigured}
              className="btn-secondary flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoadingCars ? (
                <RefreshCw className="h-4 w-4 animate-spin" />
              ) : (
                <Database className="h-4 w-4" />
              )}
              <span>
                {isLoadingCars ? 'Loading...' : 'Load Cars'}
              </span>
            </button>
          </div>

          {carsData && (
            <div className={`p-4 rounded-lg border ${
              carsData.success 
                ? 'bg-blue-50 border-blue-200' 
                : 'bg-red-50 border-red-200'
            }`}>
              <div className="flex items-start space-x-3">
                <StatusIcon success={carsData.success} />
                <div className="flex-1">
                  <p className={`font-medium ${
                    carsData.success ? 'text-blue-800' : 'text-red-800'
                  }`}>
                    {carsData.success 
                      ? `Found ${carsData.data?.length || 0} cars in database`
                      : 'Failed to load cars'
                    }
                  </p>
                  
                  {carsData.success && carsData.data && carsData.data.length > 0 && (
                    <div className="mt-2 space-y-2 max-h-60 overflow-y-auto">
                      {carsData.data.map((car: any, index: number) => (
                        <div key={index} className="text-sm text-blue-700 bg-blue-100 p-2 rounded">
                          <p><strong>ID:</strong> {car.id}</p>
                          <p><strong>Car:</strong> {car.make} {car.model} ({car.year})</p>
                          <p><strong>Price:</strong> ${car.price?.toLocaleString()}</p>
                          <p><strong>Available:</strong> {car.isAvailable ? 'Yes' : 'No'}</p>
                        </div>
                      ))}
                    </div>
                  )}
                  
                  {carsData.error && (
                    <pre className="mt-2 text-xs text-red-700 bg-red-100 p-2 rounded overflow-x-auto">
                      {carsData.error}
                    </pre>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Setup Instructions */}
        {!configInfo.isConfigured && (
          <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
            <div className="flex items-start space-x-3">
              <AlertCircle className="h-5 w-5 text-orange-600 flex-shrink-0 mt-0.5" />
              <div>
                <h4 className="font-medium text-orange-800 mb-2">Firestore Setup Required</h4>
                <div className="text-sm text-orange-700 space-y-2">
                  <p>To use Firestore, you need to enable it in Google Cloud Console:</p>
                  
                  <ol className="list-decimal list-inside space-y-1 ml-3 text-xs">
                    <li>Go to <a href="https://console.cloud.google.com/firestore" target="_blank" rel="noopener noreferrer" className="text-orange-600 underline">Firestore Console</a></li>
                    <li>Select your project: <code>{configInfo.projectId}</code></li>
                    <li>Click "Create database"</li>
                    <li>Choose "Start in production mode"</li>
                    <li>Select a location (preferably same as your Storage bucket)</li>
                    <li>The same access token from Storage will work for Firestore</li>
                  </ol>

                  <div className="bg-orange-100 p-2 rounded text-xs mt-2">
                    <p className="font-medium text-orange-800">💡 Note:</p>
                    <p className="text-orange-700">Firestore uses the same authentication as Cloud Storage. Your current access token should work for both services.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default FirestoreTest;